import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  buildFindingEvidenceTourSteps,
  isStreetViewTourStep,
} from '../../../lib/employerReview/buildFindingEvidenceTourSteps';
import type { EvidenceTourStep } from '../../../lib/employerReview/buildFindingEvidenceTourSteps';
import { buildWhatsHappeningReviewPathSteps } from '../../../lib/employerReview/buildWhatsHappeningReviewPathSteps';
import type { ReviewPathTourStep } from '../../../lib/employerReview/buildWhatsHappeningReviewPathSteps';
import { findingBriefElementId, scrollToEmployerReviewTarget } from '../../../lib/employerReview/employerReviewNavigate';
import type { ReviewReason } from '../../../lib/employerReview/reviewReason.types';

export type ActiveEvidenceTour = {
  findingId: string;
  steps: EvidenceTourStep[];
  stepIndex: number;
};

export type ActiveReviewPathTour = {
  steps: ReviewPathTourStep[];
  stepIndex: number;
};

type EmployerEvidenceTourContextValue = {
  activeTour: ActiveEvidenceTour | null;
  reviewPathTour: ActiveReviewPathTour | null;
  activeFindingTitle: string | null;
  activeCheckpointId: string | null;
  checkpointDetailsOpen: boolean;
  verificationOpen: boolean;
  gateAnalysisOpen: boolean;
  focusedFindingId: string | null;
  /** Which summary concern accordion is open during the review-path tour. */
  expandedConcernId: string | null;
  /** Bumped when the review-path tour ends so concern accordions collapse. */
  concernCollapseToken: number;
  setVerificationOpen: (open: boolean) => void;
  setGateAnalysisOpen: (open: boolean) => void;
  setCheckpointDetailsOpen: (open: boolean) => void;
  setFocusedFindingId: (findingId: string | null) => void;
  setExpandedConcernId: (findingId: string | null) => void;
  openVerification: () => void;
  openGateAnalysis: () => void;
  /** From summary: open verification, focus one concern, start checkpoint guide. */
  openVerificationForFinding: (findingId: string) => void;
  startReviewPathTour: () => void;
  closeReviewPathTour: () => void;
  nextReviewPathStep: () => void;
  prevReviewPathStep: () => void;
  startTourForFinding: (findingId: string) => void;
  closeTour: () => void;
  nextStep: () => void;
  prevStep: () => void;
  goToStep: (index: number) => void;
};

const EmployerEvidenceTourContext =
  createContext<EmployerEvidenceTourContextValue | null>(null);

export function useEmployerEvidenceTour(): EmployerEvidenceTourContextValue {
  const ctx = useContext(EmployerEvidenceTourContext);
  if (!ctx) {
    throw new Error('useEmployerEvidenceTour must be used within EmployerEvidenceTourProvider');
  }
  return ctx;
}

export function useEmployerEvidenceTourOptional(): EmployerEvidenceTourContextValue | null {
  return useContext(EmployerEvidenceTourContext);
}

export function EmployerEvidenceTourProvider({
  findings,
  issueCount,
  children,
}: {
  findings: ReviewReason[];
  issueCount: number;
  children: ReactNode;
}) {
  const [activeTour, setActiveTour] = useState<ActiveEvidenceTour | null>(null);
  const [reviewPathTour, setReviewPathTour] = useState<ActiveReviewPathTour | null>(null);
  const [checkpointDetailsOpen, setCheckpointDetailsOpen] = useState(false);
  const [verificationOpen, setVerificationOpen] = useState(false);
  const [gateAnalysisOpen, setGateAnalysisOpen] = useState(false);
  const [focusedFindingId, setFocusedFindingId] = useState<string | null>(null);
  const [expandedConcernId, setExpandedConcernId] = useState<string | null>(null);
  const [concernCollapseToken, setConcernCollapseToken] = useState(0);

  const findingById = useMemo(
    () => new Map(findings.map((finding) => [finding.id, finding])),
    [findings],
  );

  useEffect(() => {
    return () => {
      setReviewPathTour(null);
      setActiveTour(null);
      setCheckpointDetailsOpen(false);
    };
  }, []);

  const closeTour = useCallback(() => {
    setActiveTour(null);
    setCheckpointDetailsOpen(false);
  }, []);

  const beginTourForFinding = useCallback(
    (findingId: string) => {
      const finding = findingById.get(findingId);
      if (!finding) return;
      const steps = buildFindingEvidenceTourSteps(finding);
      if (!steps.length) return;
      setActiveTour({ findingId, steps, stepIndex: 0 });
      const first = steps[0];
      setCheckpointDetailsOpen(Boolean(first && !isStreetViewTourStep(first)));
    },
    [findingById],
  );

  const closeReviewPathTour = useCallback(() => {
    setReviewPathTour(null);
    setVerificationOpen(false);
    setGateAnalysisOpen(false);
    setFocusedFindingId(null);
    setExpandedConcernId(null);
    setConcernCollapseToken((token) => token + 1);
  }, []);

  const startReviewPathTour = useCallback(() => {
    const steps = buildWhatsHappeningReviewPathSteps(findings, issueCount);
    if (!steps.length) return;
    setActiveTour(null);
    setCheckpointDetailsOpen(false);
    setVerificationOpen(false);
    setGateAnalysisOpen(false);
    setFocusedFindingId(null);
    setExpandedConcernId(null);
    setReviewPathTour({ steps, stepIndex: 0 });
  }, [findings, issueCount]);

  const nextReviewPathStep = useCallback(() => {
    setReviewPathTour((prev) => {
      if (!prev) return prev;
      if (prev.stepIndex >= prev.steps.length - 1) return prev;
      return { ...prev, stepIndex: prev.stepIndex + 1 };
    });
  }, []);

  const prevReviewPathStep = useCallback(() => {
    setReviewPathTour((prev) => {
      if (!prev) return prev;
      return { ...prev, stepIndex: Math.max(0, prev.stepIndex - 1) };
    });
  }, []);

  const startTourForFinding = useCallback(
    (findingId: string) => {
      setReviewPathTour(null);
      setFocusedFindingId(findingId);
      beginTourForFinding(findingId);
    },
    [beginTourForFinding],
  );

  const openVerification = useCallback(() => {
    setVerificationOpen(true);
    setGateAnalysisOpen(false);
    setFocusedFindingId(null);
    scrollToEmployerReviewTarget('employer-verification-details');
  }, []);

  const openGateAnalysis = useCallback(() => {
    setGateAnalysisOpen(true);
    scrollToEmployerReviewTarget('employer-gate-analysis');
  }, []);

  const openVerificationForFinding = useCallback((findingId: string) => {
    setVerificationOpen(true);
    setGateAnalysisOpen(false);
    setFocusedFindingId(findingId);
    scrollToEmployerReviewTarget(findingBriefElementId(findingId));
  }, []);

  const goToStep = useCallback((index: number) => {
    setActiveTour((prev) => {
      if (!prev) return prev;
      const clamped = Math.max(0, Math.min(index, prev.steps.length - 1));
      return { ...prev, stepIndex: clamped };
    });
  }, []);

  const nextStep = useCallback(() => {
    setActiveTour((prev) => {
      if (!prev) return prev;
      if (prev.stepIndex >= prev.steps.length - 1) {
        return null;
      }
      return { ...prev, stepIndex: prev.stepIndex + 1 };
    });
  }, []);

  const prevStep = useCallback(() => {
    setActiveTour((prev) => {
      if (!prev) return prev;
      return { ...prev, stepIndex: Math.max(0, prev.stepIndex - 1) };
    });
  }, []);

  const activeFindingTitle = useMemo(() => {
    if (!activeTour) return null;
    return findingById.get(activeTour.findingId)?.title ?? null;
  }, [activeTour, findingById]);

  const activeCheckpointId = useMemo(() => {
    if (!activeTour) return null;
    const step = activeTour.steps[activeTour.stepIndex];
    if (!step || isStreetViewTourStep(step)) return null;
    return step.checkpointId ?? null;
  }, [activeTour]);

  const value = useMemo(
    () => ({
      activeTour,
      reviewPathTour,
      activeFindingTitle,
      activeCheckpointId,
      checkpointDetailsOpen,
      verificationOpen,
      gateAnalysisOpen,
      focusedFindingId,
      expandedConcernId,
      concernCollapseToken,
      setVerificationOpen,
      setGateAnalysisOpen,
      setCheckpointDetailsOpen,
      setFocusedFindingId,
      setExpandedConcernId,
      openVerification,
      openGateAnalysis,
      openVerificationForFinding,
      startReviewPathTour,
      closeReviewPathTour,
      nextReviewPathStep,
      prevReviewPathStep,
      startTourForFinding,
      closeTour,
      nextStep,
      prevStep,
      goToStep,
    }),
    [
      activeTour,
      reviewPathTour,
      activeFindingTitle,
      activeCheckpointId,
      checkpointDetailsOpen,
      verificationOpen,
      gateAnalysisOpen,
      focusedFindingId,
      expandedConcernId,
      concernCollapseToken,
      openVerification,
      openGateAnalysis,
      openVerificationForFinding,
      startReviewPathTour,
      closeReviewPathTour,
      nextReviewPathStep,
      prevReviewPathStep,
      startTourForFinding,
      closeTour,
      nextStep,
      prevStep,
      goToStep,
    ],
  );

  return (
    <EmployerEvidenceTourContext.Provider value={value}>
      {children}
    </EmployerEvidenceTourContext.Provider>
  );
}
